const PAGE_FETCH_CONCURRENCY = 6;

function hasNextPage(payload: any): boolean {
  const data = payload?.data ?? payload;
  return Boolean(
    payload?.links?.next ?? payload?.next ?? data?.links?.next ?? data?.next
  );
}

// Reads the total page count from pagination metadata, or derives it from a
// total item count. Returns 0 when the API exposes neither.
function getTotalPages(payload: any, firstPageSize: number): number {
  const sources = [
    payload,
    payload?.meta,
    payload?.pagination,
    payload?.links,
    payload?.data,
    payload?.data?.meta,
    payload?.data?.links,
  ].filter(source => source && typeof source === "object");

  for (const source of sources) {
    const pages = Number(
      source.total_pages ?? source.totalPages ?? source.num_pages
    );
    if (Number.isFinite(pages) && pages > 0) {
      return pages;
    }
  }
  if (firstPageSize > 0) {
    for (const source of sources) {
      const count = Number(
        source.count ?? source.total ?? source.total_count ?? source.totalCount
      );
      if (Number.isFinite(count) && count > 0) {
        return Math.ceil(count / firstPageSize);
      }
    }
  }
  return 0;
}

// Fetches every page of a paginated list. Page 1 is fetched first to learn the
// page count; the remaining pages are then requested in parallel instead of
// one round trip at a time. Falls back to following `next` links sequentially
// when the API doesn't report a total.
export async function fetchAllPages<T>(
  fetchPage: (page: number) => Promise<any>,
  extractItems: (payload: any) => T[]
): Promise<T[]> {
  const firstPayload = await fetchPage(1);
  const firstItems = extractItems(firstPayload);
  if (!hasNextPage(firstPayload)) {
    return firstItems;
  }

  const totalPages = getTotalPages(firstPayload, firstItems.length);
  if (totalPages > 1) {
    const pages: T[][] = [firstItems];
    let nextPage = 2;
    const worker = async () => {
      while (nextPage <= totalPages) {
        const page = nextPage++;
        pages[page - 1] = extractItems(await fetchPage(page));
      }
    };
    await Promise.all(
      Array.from(
        { length: Math.min(PAGE_FETCH_CONCURRENCY, totalPages - 1) },
        worker
      )
    );
    return pages.flat();
  }

  const all = [...firstItems];
  let page = 2;
  let payload = firstPayload;
  while (hasNextPage(payload)) {
    payload = await fetchPage(page);
    all.push(...extractItems(payload));
    page += 1;
  }
  return all;
}
